// 권리사 「할 일」 읽기·처리 — 화면 세 곳(상단바 · 작품 탭 카드 · 검수/화리 탭)이 RightsTodoContext 를 거쳐 이 파일 하나로 읽는다(2026-10-21).
//   판정은 src/lib/rightsTodo.js(순수). 여기는 Supabase 조회만.
//   🔒 새 RPC·표 없음 — 권리사가 이미 읽는 표(licensed_video · video_inspection · channel_to_video · channel)를 같은 조건으로 읽는다.
//      video_inspection 은 RLS 로 회사 행만, channel_to_video 는 ctv_read_scoped(내 회사 작품)로 읽힌다. channel 은 로그인 사용자 읽기(소유자 이메일 칸은 읽지 않는다).
//   🔒 채널 화리 처리 = 참여 채널 탭(WorkChannelList)과 같은 두 단계 — channel_to_video.whitelist_bool → 검수 건 처리 표시(서버 함수).
//      크리에이터 알림은 DB 트리거 channel_to_video_whitelist_notify 가 그대로 보낸다(이 파일은 알림을 만들지 않는다).
import { supabase } from "./supabaseClient";
import { rightsSetWhitelistProcessed } from "./inspectionWrites";
import { PENDING_INSPECTION_STATUSES } from "./workPerformance";
import { BLOCK_OPEN_STATUSES } from "./blockReport";
import { tt } from "../i18n/tt";

const CHUNK = 1000; // 서버 1회 상한
const IN_CHUNK = 150; // in(...) 목록 길이 — 주소 길이 제한

export const TODO_WORK_COLUMNS = "id, title, thumbnail, whitelist_scope, identification_code";
export const TODO_OPEN_COLUMNS = "id, video_id, video_title, status, respond_by, created_at";
export const TODO_VIDEO_WL_COLUMNS =
  "id, video_id, video_title, channel_id, channel_name, user_email, status, episode, episode_part, youtube_url, whitelist_requested, whitelist_processed, whitelist_type, created_at";
export const TODO_LINK_COLUMNS = "id, video_id, channel_id, status, rejected_bool, whitelist_bool, created_at";
export const TODO_CHANNEL_COLUMNS = "id, channel_title, channel_url, channel_id, deleted_at";
// 저작권 신고(차단 알림, 2026-10-21) — 작품 화면 [차단 알림] 숫자(VideoRightsManager.fetchBlockCounts)와 같은 조건: 회사 + 열린 상태(RLS ibr_company_select)
export const TODO_BLOCK_COLUMNS = "id, video_id, channel_id, channel_name, work_title, status, kind, reported_at, detected_at, created_at";

/** 1,000행씩 끝까지 — build(from, to) 는 range 를 붙인 쿼리를 돌려준다 */
async function readAll(build) {
  let rows = [];
  for (let from = 0; ; from += CHUNK) {
    const { data, error } = await build(from, from + CHUNK - 1);
    if (error) throw error;
    rows = rows.concat(data || []);
    if (!data || data.length < CHUNK) break;
  }
  return rows;
}

function chunks(list, size = IN_CHUNK) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/**
 * 회사의 할 일 재료 → `buildRightsTodo` 에 그대로 넘길 객체. 실패하면 throw(호출부가 이전 숫자를 지우지 않고 오류만 표시).
 */
export async function fetchRightsTodoSources(company) {
  if (!company) throw new Error("company required");

  const [works, openInspections, videoWl, blockReports] = await Promise.all([
    readAll((from, to) => supabase.from("licensed_video").select(TODO_WORK_COLUMNS).eq("company", company).order("id", { ascending: true }).range(from, to)),
    readAll((from, to) =>
      supabase.from("video_inspection").select(TODO_OPEN_COLUMNS).eq("company", company).in("status", PENDING_INSPECTION_STATUSES).order("id", { ascending: true }).range(from, to)
    ),
    readAll((from, to) =>
      supabase
        .from("video_inspection")
        .select(TODO_VIDEO_WL_COLUMNS)
        .eq("company", company)
        .eq("whitelist_requested", true)
        .or("whitelist_processed.is.null,whitelist_processed.eq.false")
        .or("status.is.null,status.neq.cancelled") // isVideoWhitelistPending 과 같게 — 상태가 빈 행도 대기로
        .order("id", { ascending: true })
        .range(from, to)
    ),
    readAll((from, to) =>
      supabase.from("inspection_block_report").select(TODO_BLOCK_COLUMNS).eq("company", company).in("status", BLOCK_OPEN_STATUSES).order("id", { ascending: true }).range(from, to)
    ),
  ]);

  const channelWorkIds = works.filter((w) => w.whitelist_scope === "channel").map((w) => w.id);
  let channelLinks = [];
  for (const ids of chunks(channelWorkIds)) {
    // eslint-disable-next-line no-await-in-loop
    const part = await readAll((from, to) =>
      supabase
        .from("channel_to_video")
        .select(TODO_LINK_COLUMNS)
        .in("video_id", ids)
        .eq("status", true)
        .or("whitelist_bool.is.null,whitelist_bool.eq.false")
        .order("id", { ascending: true })
        .range(from, to)
    );
    channelLinks = channelLinks.concat(part);
  }

  const channelIds = [...new Set(channelLinks.map((l) => l.channel_id).filter((v) => v !== null && v !== undefined))];
  let channels = [];
  for (const ids of chunks(channelIds)) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await supabase.from("channel").select(TODO_CHANNEL_COLUMNS).in("id", ids);
    if (error) throw error;
    channels = channels.concat(data || []);
  }

  return { works, openInspections, videoWhitelistRows: videoWl, channelLinks, channels, blockReports };
}

/**
 * 채널 단위 화이트리스트 처리 표시 — 참여 채널 탭(WorkChannelList)과 같은 두 단계.
 *  ① channel_to_video.whitelist_bool  ② 그 채널·작품의 검수 건 처리 표시(서버 함수 inspection_rights_set_whitelist_processed)
 * ①이 한 줄도 바뀌지 않으면(권한·이미 없는 행) 실패로 본다 — 「처리했어요」 토스트가 거짓이 되지 않게.
 * @returns {Promise<number>} 바뀐 채널 수
 */
export async function setChannelsWhitelisted(ctvIds, value = true) {
  const ids = [...new Set((ctvIds || []).filter(Boolean))];
  if (ids.length === 0) return 0;
  const { data, error } = await supabase.from("channel_to_video").update({ whitelist_bool: Boolean(value) }).in("id", ids).select("id");
  if (error) throw error;
  const changed = (data || []).length;
  if (changed === 0) throw new Error(tt("a.todo.wl.notFound", "처리할 채널을 찾지 못했어요. 새로고침한 뒤 다시 해 주세요."));
  await rightsSetWhitelistProcessed(ids, Boolean(value));
  return changed;
}
